import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigation } from '@react-navigation/native'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { Text, View } from 'react-native'
import { BankIcon, CircleArrowIcon } from '../../assets/icons'
import ConnectBankError from '../../screens/Contractor/MoreTab/Wallet/ConnectBankError'
import { useInvoiceDetailsContext } from '../../screens/Contractor/TabInvoice/InvoiceDetailsContext'
import { paths } from '../../screens/links'
import { useStore } from '../../store'
import AddPaymentMethodStore from '../../store/AddPaymentMethodStore'
import LocalStorage from '../../store/LocalStorage'
import { isWeb } from '../../utils/helpers/commonUtils'
import cookies from '../../utils/service/cooker'
import { useResponsive } from '../../utils/hooks'
import { ListItem } from '../atoms/builder-2.0/ListItem'
import { Link } from '../atoms/Link'
import BuilderBottomModal from '../molecules/BuilderBottomModal'
import { PlaidLinkOptions, usePlaidLink } from 'react-plaid-link'
import { routes } from '../../../../models'
import { ActivityIndicator } from 'react-native-paper'

interface Props {
  onNavigate(): void
  sourceInvoice?: string | null
}

export const AddBankAccountMethodBottomModal = observer((props: Props) => {
  const { t } = useTranslation()
  const navigation = useNavigation()
  const [, setSourceLoan] = useState(null)
  const [linkToken, setLinkToken] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const { sourceInvoice, onNavigate } = props
  const [errorVisible, setErrorVisible] = useState(false)
  const { userStore, screensStore } = useStore()
  const { paymentMethodsStore } = screensStore
  const isUserAuthenticated = isWeb
    ? !!cookies.get('session')
    : userStore.isAuthenticated
  const { sm } = useResponsive()

  const { setPayInvoiceModal, setInvoicePaymentMethod, setBankSuccess } =
    useInvoiceDetailsContext()
  const {
    reset,
    canGoBack,
    getPrevious,
    setModal,
    showAddBank,
    isChosenMethodTypeFromInvoiceBank,
  } = AddPaymentMethodStore

  useEffect(() => {
    LocalStorage.get('loan').then((l) =>
      setSourceLoan(l?.lms_id || l?.loanpro_id),
    )
  }, [])

  const onSuccess = useCallback(
    async (publicToken, metadata) => {
      console.log(
        `ONSUCCESS: Metadata ${JSON.stringify(metadata)} ${publicToken}`,
      )
      try {
        const response = await routes.plaid.exchangePublicToken(publicToken)
        if (response.result === 'ok') {
          userStore.fetchUser()
          await paymentMethodsStore.fetchPaymentMethods()
          const p = screensStore.paymentMethodsStore.paymentMethods
          onNavigate && onNavigate()
          if (sourceInvoice) {
            setInvoicePaymentMethod({ ...p[p.length - 1], newlyAdded: true })
            setPayInvoiceModal(true)
          } else {
            setBankSuccess(true)
          }
        }
      } catch (e: any) {
        if (e.code === 'operation/denied') {
          setErrorVisible(true)
        }
        reset()
      }
    },
    [
      userStore,
      paymentMethodsStore,
      onNavigate,
      setBankSuccess,
      sourceInvoice,
      screensStore.paymentMethodsStore.paymentMethods,
      setPayInvoiceModal,
      setInvoicePaymentMethod,
      reset,
    ],
  )

  const config: PlaidLinkOptions = useMemo(
    () => ({
      token: linkToken,
      onSuccess,
      //receivedRedirectUri
      onExit: (err, metadata) => {
        console.log(
          `Exited early. Error: ${JSON.stringify(
            err,
          )} Metadata: ${JSON.stringify(metadata)}`,
        )
      },
      onEvent: (eventName, metadata) => {
        console.log(`Event ${eventName}, Metadata: ${JSON.stringify(metadata)}`)
      },
    }),
    [linkToken, onSuccess],
  )

  const { open, ready } = usePlaidLink(config)

  // TODO: VK: use usePlaidConnect() hook
  const createToken = () => {
    setLoading(true)
    routes.plaid
      .createLinkToken()
      .then((res) => {
        setLinkToken(res.link_token)
        setLoading(false)
        reset()
      })
      .catch((err) => {
        console.log(err)
      })
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    ready && open()
  }, [ready, open])

  return (
    <>
      <ConnectBankError
        visible={errorVisible}
        onClose={() => {
          setErrorVisible(false)
        }}
      />
      <BuilderBottomModal
        visible={
          (showAddBank || isChosenMethodTypeFromInvoiceBank) &&
          isUserAuthenticated
        }
        onClose={reset}
        height={300}
        onBack={
          canGoBack
            ? () => {
                setModal(getPrevious())
              }
            : undefined
        }
      >
        <View style={{ alignItems: 'center' }}>
          <ListItem
            icon={<BankIcon />}
            title={t('ConnectYourBank')}
            subtitle={t('forFasterCheckout')}
            onPress={() => {
              createToken()
            }}
            style={{ marginTop: 18, backgroundColor: '#F5F7F8' }}
            disabled={loading}
            endIcon={loading ? <ActivityIndicator /> : <CircleArrowIcon />}
          />
          <View style={{ height: 72, justifyContent: 'center' }}>
            <Link
              title={t('linkBankManually')}
              onPress={() => {
                reset()
                setPayInvoiceModal && setPayInvoiceModal(false) // When add payment method starts from pay invoice modal
                if (sourceInvoice) {
                  navigation.navigate(paths.LinkBank, {
                    id: sourceInvoice,
                  })
                } else {
                  if (sm) {
                    navigation.navigate(paths.Console.Accounts._self, {
                      screen: paths.LinkBank,
                    })
                  } else {
                    navigation.navigate(paths.Console.More._self, {
                      screen: paths.Console.More.Wallet,
                      params: { screen: paths.LinkBank },
                    })
                  }
                }
              }}
              color={'#5F788A'}
              textStyle={{
                textDecorationLine: 'underline',
                fontWeight: '500',
                textAlign: 'center',
              }}
            />
            <Text style={{ color: '#5F788A', textAlign: 'center' }}>
              {t('takes2BusinessDays')}
            </Text>
          </View>
        </View>
      </BuilderBottomModal>
    </>
  )
})
